<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'

import { useMutation, useQuery } from '@/composables/api'
import { useAuth } from '@/composables/useAuth'
import { usePayableAmount, type PayableSource } from '@/composables/usePayableAmount'
import { usePublicToken } from '@/composables/usePublicToken'
import { useDialogsStore } from '@/stores/dialogsStore'
import type { AmortizationParams } from '@/stores/dialogsStore'
import type { PublicOcrNumber } from '@/types/_apiHidden'
import type { SettlementDocument } from '@/types/settlement-documents'
import { apiFetch } from '@/utils/api'
import type { PaymentFeedback, PaymentProvider } from './types'

import BDialog from '@/components/base/BDialog/BDialog.vue'
import PaymentOcrPage from './PaymentOcrPage.vue'
import PaymentProviderPage from './PaymentProviderPage.vue'
import PaymentRequestPage from './PaymentRequestPage.vue'
import PaymentFeedbackPage from './PaymentFeedbackPage.vue'

// Singleton payment dialog (ADA-4432) — replaces @old payments, the last legacy flow in the app.
// Opens via dialogsStore.payment from all five trigger points (the "…" menu, the inline action
// row, child invoices, the legacy surveillance view, and the login screen / /ocr deep link).
//
// Unlike the other S*Dialogs this one is mounted while LOGGED OUT: paying without signing in is
// reached from the login screen, and that flow enters on the OCR step instead of the provider
// step. `isPublic` is what separates the two throughout — it decides which endpoints are used
// and whether a public token is carried.
const { t } = useI18n()
const dialogsStore = useDialogsStore()
const queryClient = useQueryClient()
const { isAuthenticated } = useAuth()
const { getPublicToken } = usePublicToken()

const model = computed({
  get: () => dialogsStore.payment != null,
  set: (value) => {
    if (!value) dialogsStore.payment = null
  },
})

const page = ref<'ocr' | 'provider' | 'request' | 'feedback'>('provider')
const feedback = ref<PaymentFeedback | null>(null)
/** Resolved by the OCR step in the logged-out flow; the source of every figure there. */
const lookup = ref<PublicOcrNumber | null>(null)
const chosenProvider = ref<PaymentProvider | null>(null)
const chosenMobileNumber = ref<string | undefined>(undefined)

const document = computed<SettlementDocument | undefined>(() => dialogsStore.payment?.document)
/** No document means the payer never picked a debt from a list — they are paying by OCR alone. */
const isPublic = computed(() => document.value == null)

/**
 * Clear one payment's step state. Deliberately excludes `splitParams` — it is declared below and
 * would be in its temporal dead zone when the open-watcher runs immediately at setup; the
 * `document` watcher nulls it on every document change anyway.
 */
function resetState() {
  page.value = 'provider'
  feedback.value = null
  lookup.value = null
  chosenProvider.value = null
  chosenMobileNumber.value = undefined
}

// Entry page is chosen ONCE per open. Watching the payload's CONTENTS instead would send the
// OCR flow back to its input step the moment the lookup resolves.
watch(
  () => dialogsStore.payment != null,
  (isOpen) => {
    if (!isOpen) return
    // Reset on OPEN as well as on afterLeave (legacy reset its feedback on every open). An
    // interrupted leave transition — close and reopen quickly — never fires afterLeave, and a
    // surviving `lookup` outranks the payload document in payableSource: the dialog would price
    // and pay the PREVIOUS public OCR while showing the debt the payer just picked.
    resetState()
    const payload = dialogsStore.payment
    page.value = payload?.document == null ? 'ocr' : 'provider'
    // Legacy opened straight on the pending state when the OCR was already awaiting settlement
    // of an earlier payment — starting a second payment for the same debt is not what the payer
    // wants to be offered.
    if (payload?.ocr?.status === 'pending_payment') {
      feedback.value = 'pending'
      page.value = 'feedback'
    }
  },
  { immediate: true }
)

// ---------------------------------------------------------------------------
// What is being paid
// ---------------------------------------------------------------------------
const payableSource = computed<PayableSource | null>(() => {
  if (lookup.value != null) return { kind: 'public', lookup: lookup.value }
  const currentDocument = document.value
  if (currentDocument == null) return null
  return { kind: 'document', document: currentDocument, ocr: dialogsStore.payment?.ocr }
})

const { amount, currency } = usePayableAmount(payableSource)

/** The canonical OCR the provider is given — from the lookup when public, else the joined one. */
const ocrValue = computed(() => lookup.value?.ocr ?? dialogsStore.payment?.ocr?.ocr)
const ocrNumberId = computed(() => lookup.value?._id ?? dialogsStore.payment?.ocr?._id)

const paymentStatus = computed(() => {
  if (lookup.value != null) return lookup.value.payment_status
  // Signed in there is no server-derived status; the action gate already refused to open the
  // dialog for a paid debt, so the banner is only meaningful in the public flow.
  return undefined
})

// Swish prefill. Only signed in — the public flow has no user record to read.
const userQuery = useQuery('/customer--users', undefined, { enabled: computed(() => isAuthenticated.value) })
const defaultMobileNumber = computed(() => userQuery.data.value?._items?.[0]?.mobile_number_data?.mobile_number)

// ---------------------------------------------------------------------------
// D6 — split-payment shortcut, shown only when the server would actually allow a plan
// ---------------------------------------------------------------------------
const splitParams = ref<AmortizationParams | null>(null)
const canSplitPayment = computed(() => splitParams.value != null)

watch(
  document,
  async (currentDocument) => {
    splitParams.value = null
    if (currentDocument == null) return
    if (currentDocument.reference !== 'case' && currentDocument.reference !== 'debt_collection') return
    // The action targets the ORIGINATING case: a debt-collection points at its parent via the
    // nested cases[0].case FK, a plain case uses its own _id.
    const resourceId = currentDocument.cases?.[0]?.case ?? currentDocument._id
    // The shortcut is an optional affordance: a failed gate check (network/CORS rejection) just
    // hides it instead of escaping the watcher as an unhandled rejection.
    const result = await apiFetch
      .request('get', '/domain-actions/cases/new_amortization/{resource_id}', {
        params: { path: { resource_id: resourceId } },
      })
      .catch(() => null)
    if (result == null) return
    const { data, response } = result
    // 206 (+_issues) means "not allowed" — response.ok covers all of 2xx, so only a strict 200
    // may open the shortcut.
    if (response.status !== 200 || dialogsStore.payment?.document !== currentDocument) return
    splitParams.value = { ...(data as unknown as Omit<AmortizationParams, 'id'>), id: resourceId }
  },
  { immediate: true }
)

function openSplitPayment() {
  const currentDocument = document.value
  if (currentDocument == null || splitParams.value == null) return
  // Hand over to the amortization dialog: two payment-shaped dialogs open at once would be
  // ambiguous about which one the payer is completing.
  const params = splitParams.value
  model.value = false
  dialogsStore.amortization = { document: currentDocument, params }
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------
function onResolved(resolved: PublicOcrNumber) {
  lookup.value = resolved
  page.value = 'provider'
}

function onProvider(payload: { provider: PaymentProvider; mobileNumber?: string }) {
  chosenProvider.value = payload.provider
  chosenMobileNumber.value = payload.mobileNumber
  page.value = 'request'
}

// The feedback page is the outcome for both results, and onFeedback invalidates everything itself.
const demoMutation = useMutation('post', '/demo--payments', {
  meta: { notifyOnSuccess: false, notifyOnError: false, skipAutoInvalidation: true },
})
// Covers the whole submit, including the public-token mint that precedes the request.
const demoSubmitting = ref(false)

async function onDemo(demoAmount: number) {
  const id = ocrNumberId.value
  if (id == null || demoSubmitting.value) return
  demoSubmitting.value = true
  try {
    const body = isPublic.value
      ? { amount: demoAmount, ocr_number_id: id, token: await getPublicToken() }
      : { amount: demoAmount, ocr_number_id: id }
    await demoMutation.mutateAsync({ body })
    onFeedback('paid')
  } catch {
    onFeedback('failed')
  } finally {
    demoSubmitting.value = false
  }
}

function onFeedback(result: PaymentFeedback) {
  feedback.value = result
  page.value = 'feedback'
  // A completed payment changes the debt everywhere — the overview total, the card amount, the
  // ocr-numbers status the action gates read, amortization advices, surveillances. Invalidate
  // EVERYTHING, as the legacy actions_render_key watcher did: a payment is rare enough that the
  // refetch cost is irrelevant, and a hand-maintained prefix list is one nobody remembers to
  // extend — a missed prefix leaves "Betala direkt" open on a debt that was just paid.
  if (result === 'paid') void queryClient.invalidateQueries()
}

function afterLeave() {
  resetState()
  splitParams.value = null
}
</script>

<template>
  <b-dialog
    v-model="model"
    :hide-header="page === 'feedback'"
    :hide-footer="true"
    :aria-label="t('common.see_status_or_pay_directly')"
    @after-leave="afterLeave"
  >
    <template #title>{{ t('common.see_status_or_pay_directly') }}</template>

    <!-- `disabled` turns off VWindow's swipe navigation — the steps advance only
         programmatically, and swiping out of a running payment would strand it. -->
    <v-window
      v-model="page"
      disabled
    >
      <v-window-item value="ocr">
        <payment-ocr-page
          v-if="page === 'ocr'"
          :suggested-ocr="dialogsStore.payment?.suggestedOcr"
          @resolved="onResolved"
        />
      </v-window-item>

      <v-window-item value="provider">
        <payment-provider-page
          :amount="amount"
          :currency="currency"
          :ocr="ocrValue"
          :payment-status="paymentStatus"
          :default-mobile-number="defaultMobileNumber"
          :can-split-payment="canSplitPayment"
          :demo-loading="demoSubmitting"
          @provider="onProvider"
          @demo="onDemo"
          @split-payment="openSplitPayment"
        />
      </v-window-item>

      <v-window-item value="request">
        <!-- Mounted only while on this step so leaving it (or closing the dialog) tears the
             transaction down — that teardown is what cancels an abandoned payment (D14). -->
        <payment-request-page
          v-if="page === 'request' && chosenProvider != null && ocrValue != null"
          :provider="chosenProvider"
          :ocr="ocrValue"
          :amount="amount"
          :currency="currency"
          :mobile-number="chosenMobileNumber"
          :is-public="isPublic"
          @feedback="onFeedback"
        />
      </v-window-item>

      <v-window-item value="feedback">
        <payment-feedback-page
          :feedback="feedback"
          :is-public="isPublic"
          @close="model = false"
        />
      </v-window-item>
    </v-window>
  </b-dialog>
</template>
